import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Building2, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

// Public form — uses axios without credentials (no auth cookie needed)
const apiBase = process.env.REACT_APP_BACKEND_URL;

export default function RequestDemoPage() {
  const [form, setForm] = useState({
    apartment_name: "", contact_name: "", contact_email: "",
    contact_phone: "", city: "", flat_count: "", role_in_apartment: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  function on(k) { return (e) => setForm({ ...form, [k]: e.target.value }); }

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      await axios.post(`${apiBase}/api/leads`, {
        ...form,
        flat_count: form.flat_count ? Number(form.flat_count) : null,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again");
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] paper-grain">
      <header className="px-6 py-5 max-w-4xl mx-auto flex items-center justify-between">
        <Link to="/login" className="flex items-center gap-2 text-stone-700 hover:text-[#C05640]">
          <div className="w-9 h-9 rounded-xl bg-[#C05640] text-white grid place-items-center">
            <Building2 size={20} />
          </div>
          <div>
            <div className="font-serif-display font-semibold leading-none">NivasOne</div>
            <div className="text-[10px] text-stone-500 tracking-wide">Apartment OS</div>
          </div>
        </Link>
        <Link to="/login" className="text-sm text-stone-600 hover:text-[#C05640] inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Back to sign in
        </Link>
      </header>

      <main className="max-w-2xl mx-auto px-6 pb-16">
        {done ? (
          <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center" data-testid="demo-done">
            <CheckCircle2 size={48} className="mx-auto text-green-600" />
            <h1 className="font-serif-display text-3xl font-medium mt-4">Got it!</h1>
            <p className="text-stone-600 mt-2">Thank you, {form.contact_name.split(" ")[0]}. We've received your request for {form.apartment_name}.</p>
            <p className="text-stone-600 mt-1 text-sm">Our team usually responds within one working day. We'll set up your apartment and email you the login details.</p>
            <Link to="/login" className="inline-block mt-6 text-[#C05640] font-medium underline-offset-2 hover:underline">Back to sign in →</Link>
          </div>
        ) : (
          <>
            <h1 className="font-serif-display text-4xl md:text-5xl font-medium tracking-tight mt-6">Bring NivasOne to your apartment</h1>
            <p className="text-stone-600 mt-3 max-w-lg">Tell us a little about your community. We onboard buildings one at a time during the pilot — usually within a working day.</p>

            <form onSubmit={submit} className="mt-8 bg-white border border-stone-200 rounded-2xl p-6 md:p-8 space-y-4" data-testid="demo-form">
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Apartment name *</Label>
                  <Input required value={form.apartment_name} onChange={on("apartment_name")} data-testid="demo-apartment" className="h-11 mt-1.5" placeholder="Sundar Apartments" />
                </div>
                <div>
                  <Label>City</Label>
                  <Input value={form.city} onChange={on("city")} data-testid="demo-city" className="h-11 mt-1.5" placeholder="Bengaluru" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Your name *</Label>
                  <Input required value={form.contact_name} onChange={on("contact_name")} data-testid="demo-name" className="h-11 mt-1.5" />
                </div>
                <div>
                  <Label>Your role</Label>
                  <select value={form.role_in_apartment} onChange={on("role_in_apartment")} data-testid="demo-role"
                    className="w-full h-11 mt-1.5 rounded-xl border border-stone-300 px-3 bg-white">
                    <option value="">— Select —</option>
                    <option value="committee_member">Committee member</option>
                    <option value="resident">Resident</option>
                    <option value="property_manager">Property manager</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Email *</Label>
                  <Input type="email" required value={form.contact_email} onChange={on("contact_email")} data-testid="demo-email" className="h-11 mt-1.5" />
                </div>
                <div>
                  <Label>Phone (optional)</Label>
                  <Input value={form.contact_phone} onChange={on("contact_phone")} data-testid="demo-phone" className="h-11 mt-1.5" placeholder="+91-…" />
                </div>
              </div>
              <div>
                <Label>How many flats?</Label>
                <Input type="number" value={form.flat_count} onChange={on("flat_count")} data-testid="demo-flats" className="h-11 mt-1.5" placeholder="e.g. 24" />
                <p className="text-[11px] text-stone-500 mt-1">NivasOne is built for 4–40 flat buildings.</p>
              </div>
              <div>
                <Label>Anything you'd like us to know? (optional)</Label>
                <Textarea rows={3} value={form.message} onChange={on("message")} data-testid="demo-msg" className="mt-1.5" placeholder="What's working / not working in your current WhatsApp group?" />
              </div>
              <Button type="submit" disabled={busy} data-testid="demo-submit"
                className="w-full h-12 rounded-full bg-[#C05640] hover:bg-[#A54734] text-base font-semibold">
                {busy ? "Sending…" : "Request a demo"}
              </Button>
              <p className="text-[11px] text-stone-500 text-center">We'll never share your details. This goes straight to the NivasOne team for review.</p>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
